import React, { useEffect, useMemo, useState } from "react";
import {
  FaSave,
  FaTimes,
  FaPlusCircle,
  FaBoxOpen,
  FaEuroSign,
  FaLink,
  FaLayerGroup,
} from "react-icons/fa";
import { FaRepeat } from "react-icons/fa6";
import { addProduct } from "../../../../services/productsServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const initialForm = {
  nom_product: "",
  contenu_product: "",
  tarif_product: "",
  periodicite_product: "",
  categorie_product: "",
  payment_stripe_link: "",
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-indigo-900/40";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

const AddProductModal = ({ isOpen, onClose, refreshList }) => {
  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(initialForm);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const amountPreview = useMemo(() => {
    const value = Number(formData.tarif_product);
    if (!Number.isFinite(value) || value <= 0) return "—";
    return value.toLocaleString("fr-FR", {
      style: "currency",
      currency: "EUR",
    });
  }, [formData.tarif_product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({ ...prev, contenu_product: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom_product || !formData.tarif_product) {
      toast.error("Veuillez renseigner au moins le nom et le tarif.");
      return;
    }

    try {
      setSaving(true);
      await addProduct(formData);
      toast.success("Produit ajouté avec succès");
      refreshList?.();
      onClose?.();
    } catch (error) {
      toast.error("Erreur lors de l'ajout du produit");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200]">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-[3px]"
        onClick={onClose}
      />

      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-6">
        <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_30px_90px_-30px_rgba(0,0,0,0.45)] dark:bg-slate-900">
          {/* Header */}
          <div className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.26),transparent_28%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.22),transparent_24%),linear-gradient(135deg,#0f172a_0%,#1e293b_45%,#312e81_100%)]" />
            <div className="relative px-5 py-5 sm:px-6 sm:py-6 text-white">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                    <FaPlusCircle />
                    Nouveau produit
                  </div>

                  <h3 className="mt-3 text-2xl font-semibold tracking-tight">
                    Ajouter un produit
                  </h3>

                  <p className="mt-1 max-w-2xl text-sm text-white/75">
                    Crée une nouvelle offre avec son contenu, son tarif, sa
                    catégorie, sa périodicité et son lien de paiement.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950/60">
            <form onSubmit={handleSubmit} className="p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                <div className="xl:col-span-8 space-y-6">
                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Informations produit"
                      subtitle="Nom, tarif, périodicité, catégorie et lien Stripe."
                    />

                    <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Field>
                        <Label required>Nom du produit</Label>
                        <div className="relative">
                          <FaBoxOpen className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            className={`${inputClass} pl-10`}
                            type="text"
                            name="nom_product"
                            value={formData.nom_product}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label required>Tarif</Label>
                        <div className="relative">
                          <FaEuroSign className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            className={`${inputClass} pl-10`}
                            type="number"
                            step="0.01"
                            min="0"
                            name="tarif_product"
                            value={formData.tarif_product}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label>Périodicité</Label>
                        <div className="relative">
                          <FaRepeat className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            className={`${inputClass} pl-10`}
                            type="text"
                            name="periodicite_product"
                            value={formData.periodicite_product}
                            onChange={handleChange}
                            placeholder="Mensuel, Annuel, Unique..."
                          />
                        </div>
                      </Field>

                      <Field>
                        <Label>Catégorie</Label>
                        <div className="relative">
                          <FaLayerGroup className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            className={`${inputClass} pl-10`}
                            type="text"
                            name="categorie_product"
                            value={formData.categorie_product}
                            onChange={handleChange}
                            placeholder="SaaS, Site web, Formation..."
                          />
                        </div>
                      </Field>

                      <Field className="md:col-span-2">
                        <Label>Lien Stripe</Label>
                        <div className="relative">
                          <FaLink className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            className={`${inputClass} pl-10`}
                            type="text"
                            name="payment_stripe_link"
                            value={formData.payment_stripe_link}
                            onChange={handleChange}
                            placeholder="https://..."
                          />
                        </div>
                      </Field>
                    </div>
                  </section>

                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Contenu commercial"
                      subtitle="Décris précisément le produit, ses avantages et ses options."
                    />

                    <div className="mt-5">
                      <Label>Contenu</Label>
                      <div className="mt-2 overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-sm dark:border-slate-700">
                        <ReactQuill
                          value={formData.contenu_product}
                          onChange={handleQuillChange}
                          modules={quillModules}
                          formats={quillFormats}
                          className="product-quill"
                          theme="snow"
                        />
                      </div>
                    </div>
                  </section>
                </div>

                <div className="xl:col-span-4 space-y-6">
                  <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Résumé"
                      subtitle="Aperçu rapide avant enregistrement."
                    />

                    <div className="mt-5 space-y-4">
                      <SummaryRow
                        icon={<FaBoxOpen />}
                        label="Produit"
                        value={formData.nom_product || "Non renseigné"}
                      />
                      <SummaryRow
                        icon={<FaEuroSign />}
                        label="Tarif"
                        value={amountPreview}
                      />
                      <SummaryRow
                        icon={<FaRepeat />}
                        label="Périodicité"
                        value={formData.periodicite_product || "Non renseignée"}
                      />
                      <SummaryRow
                        icon={<FaLayerGroup />}
                        label="Catégorie"
                        value={formData.categorie_product || "Non renseignée"}
                      />
                      <SummaryRow
                        icon={<FaLink />}
                        label="Stripe"
                        value={
                          formData.payment_stripe_link
                            ? "Lien renseigné"
                            : "Aucun lien"
                        }
                      />
                    </div>
                  </section>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 dark:border-slate-800 dark:bg-slate-900/95">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                onClick={handleSubmit}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-600 px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Ajouter le produit"}
              </button>
            </div>
          </div>

          <style>{`
            .product-quill .ql-toolbar.ql-snow {
              border: 0;
              border-bottom: 1px solid rgb(226 232 240);
              padding: 12px;
              background: linear-gradient(to right, rgba(248,250,252,0.9), rgba(241,245,249,0.9));
            }

            .product-quill .ql-container.ql-snow {
              border: 0;
              min-height: 180px;
              font-size: 0.95rem;
            }

            .product-quill .ql-editor {
              min-height: 180px;
              color: #0f172a;
            }

            .dark .product-quill .ql-toolbar.ql-snow {
              background: rgba(15, 23, 42, 0.92);
              border-bottom: 1px solid rgb(51 65 85);
            }

            .dark .product-quill .ql-container.ql-snow {
              background: rgb(15 23 42);
            }

            .dark .product-quill .ql-editor {
              color: #e2e8f0;
            }

            .dark .product-quill .ql-stroke {
              stroke: #cbd5e1;
            }

            .dark .product-quill .ql-fill {
              fill: #cbd5e1;
            }

            .dark .product-quill .ql-picker {
              color: #cbd5e1;
            }
          `}</style>
        </div>
      </div>
    </div>
  );
};

function SectionTitle({ title, subtitle }) {
  return (
    <div>
      <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h4>
      {subtitle ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function Field({ children }) {
  return <div>{children}</div>;
}

function Label({ children, required = false }) {
  return (
    <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-200">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
          {label}
        </div>
        <div className="mt-1 break-words text-sm font-medium text-slate-900 dark:text-slate-50">
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

export default AddProductModal;